import React from 'react';

const ON_HOLD_REASONS = [
  { value: 'weather', label: 'Weather prevented install' },
  { value: 'incorrect_product', label: 'Incorrect product' },
  { value: 'material_missing', label: 'Material missing' },
  { value: 'customer_not_ready', label: 'Customer not ready' },
  { value: 'site_issues', label: 'Site not ready' },
  { value: 'crew_incomplete', label: 'Crew couldn\'t complete' },
  { value: 'other', label: 'Other (see notes)' }
];

export default function JobBasicsSection({ formData, onChange }) {
  const handleStatusChange = (newStatus) => {
    // DON'T clear scheduled date when changing to "On Hold"
    // The date is preserved for history tracking
    onChange({ status: newStatus });
    
    // Clear on hold reason if changing away from "On Hold"
    if (formData.status === 'on_hold' && newStatus !== 'on_hold') {
      onChange({ onHoldReason: null });
    }
  };

  return (
    <div>
      <h3 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: 'var(--text-primary)',
        marginBottom: '16px'
      }}>
        Job Basics
      </h3>

      {/* Location/Town */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          marginBottom: '6px'
        }}>
          Location/Town *
        </label>
        <input
          type="text"
          value={formData.name || ''}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Milbank, SD"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--input-border)',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none'
          }}
          onFocus={(e) => e.target.style.borderColor = 'var(--brand-brown)'}
          onBlur={(e) => e.target.style.borderColor = 'var(--input-border)'}
        />
        <div style={{
          fontSize: '11px',
          color: 'var(--text-tertiary)',
          marginTop: '4px'
        }}>
          City or town where the job is located
        </div>
      </div>

      {/* Contract Number */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          marginBottom: '6px'
        }}>
          Contract Number *
        </label>
        <input
          type="text"
          value={formData.contractNumber || ''}
          onChange={(e) => onChange({ contractNumber: e.target.value })}
          placeholder="8800"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--input-border)',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none'
          }}
          onFocus={(e) => e.target.style.borderColor = 'var(--brand-brown)'}
          onBlur={(e) => e.target.style.borderColor = 'var(--input-border)'}
        />
      </div>

      {/* Sold Date */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          marginBottom: '6px'
        }}>
          Sold Date *
        </label>
        <input
          type="date"
          value={formData.soldDate || ''}
          onChange={(e) => onChange({ soldDate: e.target.value })}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--input-border)',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none'
          }}
          onFocus={(e) => e.target.style.borderColor = 'var(--brand-brown)'}
          onBlur={(e) => e.target.style.borderColor = 'var(--input-border)'}
        />
      </div>

      {/* Scheduled Date (optional, shows info message if On Hold) */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          marginBottom: '6px'
        }}>
          Scheduled Date (optional)
        </label>
        <input
          type="date"
          value={formData.scheduledDate || ''}
          onChange={(e) => onChange({ scheduledDate: e.target.value })}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--input-border)',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none'
          }}
          onFocus={(e) => e.target.style.borderColor = 'var(--brand-brown)'}
          onBlur={(e) => e.target.style.borderColor = 'var(--input-border)'}
        />
        {formData.status === 'on_hold' && formData.scheduledDate && (
          <div style={{
            fontSize: '11px',
            color: 'var(--tag-orange-text)',
            marginTop: '4px',
            padding: '8px',
            background: 'var(--note-bg)',
            borderRadius: '6px',
            border: '1px solid var(--note-border)'
          }}>
            ℹ️ This date is preserved for history. The job will show in orange on the calendar until rescheduled.
          </div>
        )}
      </div>

      {/* Job Type */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          marginBottom: '6px'
        }}>
          Job Type *
        </label>
        <select
          value={formData.jobType || 'install'}
          onChange={(e) => onChange({ jobType: e.target.value })}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--input-border)',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none',
            background: 'var(--bg-tertiary)'
          }}
          onFocus={(e) => e.target.style.borderColor = 'var(--brand-brown)'}
          onBlur={(e) => e.target.style.borderColor = 'var(--input-border)'}
        >
          <option value="install">Install</option>
          <option value="repair">Repair</option>
          <option value="delivery">Delivery Only</option>
          <option value="pickup">Pickup at Shop</option>
        </select>
      </div>

      {/* Status */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          marginBottom: '6px'
        }}>
          Status
        </label>
        <select
          value={formData.status || 'sold'}
          onChange={(e) => handleStatusChange(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--input-border)',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none',
            background: 'var(--bg-tertiary)'
          }}
          onFocus={(e) => e.target.style.borderColor = 'var(--brand-brown)'}
          onBlur={(e) => e.target.style.borderColor = 'var(--input-border)'}
        >
          <option value="sold">Sold</option>
          <option value="awaiting_production">Awaiting Production</option>
          <option value="in_production">In Production</option>
          <option value="ready_for_install">Ready for Install</option>
          <option value="ready_for_pickup">Ready for Pickup</option>
          <option value="scheduled">Scheduled</option>
          <option value="on_hold">On Hold</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* On Hold Reason (conditional) */}
      {formData.status === 'on_hold' && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '6px'
          }}>
            Reason for Hold * (Internal Only)
          </label>
          <select
            value={formData.onHoldReason || ''}
            onChange={(e) => onChange({ onHoldReason: e.target.value })}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid var(--input-border)',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              background: 'var(--bg-tertiary)'
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--brand-brown)'}
            onBlur={(e) => e.target.style.borderColor = 'var(--input-border)'}
          >
            <option value="">Select reason...</option>
            {ON_HOLD_REASONS.map(reason => (
              <option key={reason.value} value={reason.value}>
                {reason.label}
              </option>
            ))}
          </select>
          <div style={{
            fontSize: '11px',
            color: 'var(--text-secondary)',
            marginTop: '6px',
            padding: '8px',
            background: 'var(--note-bg)',
            borderRadius: '6px',
            border: '1px solid var(--note-border)'
          }}>
            ⚠️ Customer will be notified via SMS/Email. Reason is for internal tracking only.
          </div>
        </div>
      )}
    </div>
  );
}